import { getSecureAtomicTime } from '../services/firebase';
import { normalizeCategory, normalizePremiumEquivalentCategory } from './productRules';

export const formatPrice = (price, currency = 'USD') => {
  if (typeof price === 'string') return price;
  if (typeof price === 'number') {
    const amount = Number.isFinite(price) ? price : 0;
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
      }).format(amount);
    } catch (error) {
      if (error instanceof RangeError) {
        return `${amount.toFixed(2)} ${String(currency || '').toUpperCase() || 'USD'}`;
      }
      throw error;
    }
  }
  return '$0.00';
};

const MAX_CUSTOM_DURATION_DAYS = 3650;
const DURATION_ALIASES = {
  monthly: 'monthly',
  month: 'monthly',
  '1month': 'monthly',
  '1months': 'monthly',
  '1m': 'monthly',
  quarterly: 'quarterly',
  quaterly: 'quarterly',
  quartery: 'quarterly',
  quarter: 'quarterly',
  '3month': 'quarterly',
  '3months': 'quarterly',
  '3m': 'quarterly',
  yearly: 'yearly',
  year: 'yearly',
  annual: 'yearly',
  annually: 'yearly',
  '1year': 'yearly',
  '1years': 'yearly',
  '1y': 'yearly',
  lifetime: 'lifetime',
};

const parsePositiveDurationDays = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  const days = Math.round(numeric * 10) / 10;
  if (days <= 0) return null;
  return Math.min(days, MAX_CUSTOM_DURATION_DAYS);
};

const formatDayCount = (value) => {
  const days = parsePositiveDurationDays(value);
  if (days == null) return '';
  return Number.isInteger(days) ? String(days) : days.toFixed(1);
};

export const normalizeDurationKey = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const normalized = raw.toLowerCase();
  const compact = normalized.replace(/[_\s]+/g, '');
  const aliasKey = compact.replace(/-/g, '');

  if (DURATION_ALIASES[aliasKey]) {
    return DURATION_ALIASES[aliasKey];
  }

  const dayMatch = compact.match(/^(\d{1,4}(?:\.\d)?)(d|day|days)$/);
  if (dayMatch) {
    const days = parsePositiveDurationDays(dayMatch[1]);
    return days ? `${formatDayCount(days)}d` : '';
  }

  return normalized.replace(/\s+/g, ' ');
};

export const getDurationDays = (value) => {
  const normalized = normalizeDurationKey(value);
  if (normalized === 'monthly') return 30;
  if (normalized === 'quarterly') return 90;
  if (normalized === 'yearly') return 365;
  if (normalized === 'lifetime') return null;

  const dayMatch = normalized.match(/^(\d{1,4}(?:\.\d)?)d$/);
  if (dayMatch) return parsePositiveDurationDays(dayMatch[1]);

  return null;
};

export const formatDurationLabel = (value) => {
  const normalized = normalizeDurationKey(value);
  if (normalized === 'monthly') return 'Monthly';
  if (normalized === 'quarterly') return 'Quarterly';
  if (normalized === 'yearly') return 'Yearly';
  if (normalized === 'lifetime') return 'Lifetime';

  const dayMatch = normalized.match(/^(\d{1,4}(?:\.\d)?)d$/);
  if (dayMatch) {
    const days = Number(dayMatch[1]);
    return `${days} day${days === 1 ? '' : 's'}`;
  }

  return String(value || '')
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

export const sortDurationKeys = (keys = []) => {
  return Array.from(new Set(keys.filter(Boolean))).sort((left, right) => {
    const leftDays = getDurationDays(left);
    const rightDays = getDurationDays(right);

    if (leftDays === null && rightDays !== null) return 1;
    if (leftDays !== null && rightDays === null) return -1;
    if (leftDays !== null && rightDays !== null && leftDays !== rightDays) return leftDays - rightDays;

    return formatDurationLabel(left).localeCompare(formatDurationLabel(right));
  });
};

export const resolvePriceForValidity = (priceMap = {}, validity) => {
  if (!priceMap || typeof priceMap !== 'object') return null;

  const rawValidity = String(validity || '').trim();
  const normalizedValidity = normalizeDurationKey(rawValidity);
  const candidates = [
    rawValidity,
    rawValidity.toLowerCase(),
    normalizedValidity,
  ];

  if (normalizedValidity === 'monthly') candidates.push('monthly', '1m');
  if (normalizedValidity === 'quarterly') candidates.push('quarterly', '3m');
  if (normalizedValidity === 'yearly') candidates.push('yearly', '1y');

  for (const key of candidates.filter(Boolean)) {
    if (typeof priceMap[key] === 'number') return priceMap[key];
    if (typeof priceMap[key] === 'string') {
      const parsed = parseFloat(priceMap[key].replace(/[^0-9.]/g, ''));
      if (Number.isFinite(parsed)) return parsed;
    }
  }

  const match = Object.entries(priceMap).find(([key]) => normalizeDurationKey(key) === normalizedValidity);
  if (!match) return null;

  const value = match[1];
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value.replace(/[^0-9.]/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

export const durationKeyFromDays = (days) => {
  const normalizedDays = parsePositiveDurationDays(days);
  return normalizedDays == null ? '' : `${formatDayCount(normalizedDays)}d`;
};

export const roundCurrencyToOneDecimal = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  const rounded = Math.round(numeric * 10) / 10;
  return rounded > 0 && rounded < 0.1 ? 0.1 : rounded;
};

const parsePositivePriceEntry = (value) => {
  const price = typeof value === 'number'
    ? value
    : parseFloat(String(value ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(price) && price > 0 ? price : null;
};

export const resolveAdjustedPriceForValidity = (priceMap = {}, validity) => {
  const exactPrice = resolvePriceForValidity(priceMap, validity);
  const requestedDays = getDurationDays(validity);
  if (exactPrice !== null) {
    return {
      price: Number(exactPrice),
      isProrated: false,
      days: requestedDays,
      sourceValidity: normalizeDurationKey(validity)
    };
  }

  if (requestedDays == null || requestedDays <= 0 || !priceMap || typeof priceMap !== 'object') {
    return { price: null, isProrated: false, days: requestedDays, sourceValidity: '' };
  }

  const pricedDurations = Object.entries(priceMap)
    .map(([key, value]) => {
      const price = parsePositivePriceEntry(value);
      const days = getDurationDays(key);
      return {
        key,
        normalizedKey: normalizeDurationKey(key),
        price,
        days
      };
    })
    .filter((entry) => Number.isFinite(entry.price) && entry.price > 0 && entry.days !== null && entry.days > 0)
    .sort((left, right) => left.days - right.days);

  if (pricedDurations.length === 0) {
    return { price: null, isProrated: false, days: requestedDays, sourceValidity: '' };
  }

  const source = pricedDurations.find((entry) => entry.days >= requestedDays) || pricedDurations[pricedDurations.length - 1];
  const adjustedPrice = roundCurrencyToOneDecimal((source.price / source.days) * requestedDays);
  return {
    price: adjustedPrice,
    isProrated: true,
    days: requestedDays,
    sourceValidity: source.normalizedKey
  };
};

export const resolveAddonPriceForBaseValidity = (addonPriceMap = {}, basePriceMap = {}, validity) => {
  const exactAddonPrice = resolvePriceForValidity(addonPriceMap, validity);
  const requestedDays = getDurationDays(validity);
  const hasPositiveAddonPrice = Object.values(addonPriceMap || {}).some((value) => parsePositivePriceEntry(value) !== null);

  if (exactAddonPrice !== null && (Number(exactAddonPrice) > 0 || !hasPositiveAddonPrice)) {
    return {
      price: Number(exactAddonPrice),
      isProrated: false,
      isRatioAdjusted: false,
      days: requestedDays,
      sourceValidity: normalizeDurationKey(validity)
    };
  }

  if (!hasPositiveAddonPrice || !basePriceMap || typeof basePriceMap !== 'object') {
    return { price: null, isProrated: false, isRatioAdjusted: false, days: requestedDays, sourceValidity: '' };
  }

  const exactBasePrice = resolvePriceForValidity(basePriceMap, validity);
  const adjustedBasePrice = exactBasePrice !== null && Number(exactBasePrice) > 0
    ? Number(exactBasePrice)
    : resolveAdjustedPriceForValidity(basePriceMap, validity).price;

  if (!Number.isFinite(adjustedBasePrice) || adjustedBasePrice <= 0) {
    return resolveAdjustedPriceForValidity(addonPriceMap, validity);
  }

  const ratioSources = Object.entries(addonPriceMap)
    .map(([key, value]) => {
      const addonPrice = parsePositivePriceEntry(value);
      const basePrice = resolvePriceForValidity(basePriceMap, key);
      const days = getDurationDays(key);
      return {
        key,
        normalizedKey: normalizeDurationKey(key),
        addonPrice,
        basePrice,
        days
      };
    })
    .filter((entry) => (
      Number.isFinite(entry.addonPrice) &&
      entry.addonPrice > 0 &&
      Number.isFinite(Number(entry.basePrice)) &&
      Number(entry.basePrice) > 0
    ));

  if (ratioSources.length === 0) {
    return resolveAdjustedPriceForValidity(addonPriceMap, validity);
  }

  const source = requestedDays === null
    ? ratioSources.find((entry) => entry.normalizedKey === 'lifetime') || ratioSources[0]
    : ratioSources
        .filter((entry) => entry.days !== null && entry.days > 0)
        .sort((left, right) => Math.abs(left.days - requestedDays) - Math.abs(right.days - requestedDays))[0] || ratioSources[0];

  const ratio = source.addonPrice / Number(source.basePrice);
  const adjustedPrice = roundCurrencyToOneDecimal(adjustedBasePrice * ratio);
  return {
    price: adjustedPrice,
    isProrated: true,
    isRatioAdjusted: true,
    days: requestedDays,
    sourceValidity: source.normalizedKey,
    ratio
  };
};

export const formatDate = (date) => {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(date));
};

export const generateId = () => {
  return Math.random().toString(36).slice(2, 11).toUpperCase();
};

// Generate order ID matching the pattern from Firebase export
export const buildOrderId = () => {
  const segment1 = generateId().padEnd(8, 'X').slice(0, 8); // 8 characters
  const segment2 = generateId().padEnd(5, 'F').slice(0, 5);  // 5 characters
  return `ORD-${segment1}-${segment2}`;
};

// Generate extension order ID with EXT prefix
export const buildExtensionOrderId = () => {
  const segment1 = generateId().padEnd(8, 'X').slice(0, 8); // 8 characters
  const segment2 = generateId().padEnd(5, 'F').slice(0, 5);  // 5 characters
  return `EXT-${segment1}-${segment2}`;
};

export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export const truncateText = (text, length = 100) => {
  if (!text) return '';
  if (text.length <= length) return text;
  return text.slice(0, length) + '...';
};

export const slugify = (text) => {
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
};

// Resolve display-safe fields from mixed shapes
export const getDisplayName = (p) => p?.name || p?.Name || 'Product';
export const getDisplayImage = (p) =>
  p?.image ||
  p?.['icon link'] ||
  p?.icon ||
  p?.thumbnail ||
  '/icon.png';

export const getScriptTypeLabels = (product) => {
  const hasLua = Boolean(product?.scriptFormats?.lua);
  const hasJson = Boolean(product?.scriptFormats?.json);

  if (hasLua && hasJson) {
    return ['game_guardian_script', 'mod_menu_script'];
  }
  if (hasLua) {
    return ['game_guardian_script'];
  }
  if (hasJson) {
    return ['mod_menu_script'];
  }
  return [];
};

// Normalize a single product node from RTDB with new structure
export const normalizeProductFromRTDB = (category, id, node) => {
  if (!node || typeof node !== 'object') return null;
  const normalizedCategory = normalizeCategory(category || node?.category || node?.Category);

  const priceObj = node?.Price || {};
  const numericPrices = Object.values(priceObj)
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0);
  const monthly = Number(priceObj?.monthly ?? priceObj?.Monthly);
  const fallbackPrice = Number.isFinite(monthly) && monthly > 0 ? monthly : numericPrices.length ? Math.min(...numericPrices) : 0;

  const productIds = Array.isArray(node?.Products) ? node.Products.filter(Boolean).map(String) : [];
  const addons = Object.entries(node?.Addons || node?.addons || {})
    .map(([addonId, addonNode]) => {
      if (!addonNode || typeof addonNode !== 'object') return null;
      const addonPriceObj = addonNode.Price || addonNode.price || {};
      const addonPrices = Object.values(addonPriceObj)
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0);
      const addonMonthly = Number(addonPriceObj?.monthly ?? addonPriceObj?.Monthly);
      const addonFallbackPrice = Number.isFinite(addonMonthly) && addonMonthly > 0
        ? addonMonthly
        : addonPrices.length ? Math.min(...addonPrices) : 0;

      return {
        id: String(addonId),
        productId: id,
        parentProductId: id,
        parentProductName: node?.Name || node?.name || 'Product',
        name: addonNode.Name || addonNode.name || String(addonId).replace(/[_-]+/g, ' '),
        description: addonNode.Description || addonNode.description || '',
        status: addonNode.Status || addonNode.status || 'Active',
        image: addonNode['icon link'] || addonNode.iconUrl || addonNode.image || node?.['icon link'] || node?.image || '',
        pricingRaw: addonPriceObj,
        price: addonFallbackPrice,
        sortOrder: Number(addonNode.sortOrder || 0) || 0,
        requiresBaseProduct: addonNode.requiresBaseProduct !== false,
      };
    })
    .filter(Boolean)
    .sort((left, right) => (left.sortOrder - right.sortOrder) || String(left.name || '').localeCompare(String(right.name || '')));

  // Handle features array properly
  const features = Array.isArray(node?.Features)
    ? node.Features.filter((f) => f && typeof f === 'string')
    : (typeof node?.Features === 'string'
      ? node.Features.split(',').map((f) => f.trim()).filter(Boolean)
      : []);

  // Handle testedSites array properly on product (just like features)
  const rawTestedSites = node?.TestedSites || node?.testedSites || [];
  const testedSites = (
    Array.isArray(rawTestedSites)
      ? rawTestedSites
      : (typeof rawTestedSites === 'object' && rawTestedSites !== null ? Object.values(rawTestedSites) : [])
  )
    .map((site) => {
      if (!site) return null;
      if (typeof site === 'string') {
        return { name: site.trim(), url: '', status: 'working', accuracyRate: '', notes: '', lastTested: '' };
      }
      return {
        name: (site.name || site.Name || '').trim(),
        url: (site.url || site.Url || '').trim(),
        status: site.status || 'working',
        accuracyRate: (site.accuracyRate || site.accuracy || '').trim(),
        notes: (site.notes || site.description || '').trim(),
        lastTested: site.lastTested || '',
        iconUrl: site.iconUrl || ''
      };
    })
    .filter((s) => s && s.name);

  // Convert timestamps to Date objects for proper sorting
  const createdAtTimestamp = node?.createdAt ? new Date(node.createdAt).getTime() : Date.now();
  const updatedAtTimestamp = node?.updatedAt ? new Date(node.updatedAt).getTime() : createdAtTimestamp;

  return {
    id,
    category: normalizedCategory,
    name: node?.Name || node?.name || 'Product',
    description: node?.Description || node?.description || '',
    features,
    testedSites,
    status: node?.Status || node?.status || 'Active',
    version: node?.Version || node?.version || '1.0',
    createdAt: createdAtTimestamp,
    updatedAt: updatedAtTimestamp,
    image: node?.['icon link'] || node?.image || '/icon.png',
    tutorial: node?.['Tutorial Video Link'] || node?.tutorial || '',
    pricingRaw: priceObj,
    price: fallbackPrice,
    originalPrice: undefined,
    featured: Boolean(node?.featured || node?.Featured || Math.random() > 0.8),
    popularity: typeof node?.popularity === 'number' ? node.popularity : Math.floor(Math.random() * 100),
    Products: productIds,
    bundleItems: productIds,
    Type: node?.OtherType || node?.Type || node?.type || 'internal',
    type: node?.OtherType || node?.Type || node?.type || 'internal',
    ExternalLink: node?.ExternalLink || node?.externalLink || '',
    freeDownloadLink: node?.FreeDownloadLink || node?.downloadLink || node?.DownloadLink || '',
    linkedProductId: node?.LinkedProductId || node?.linkedProductId || '',
    addons,
    EmbeddedScript: node?.EmbeddedScript || null,
    scriptFormats: {
      lua: Boolean(node?.scriptFormats?.lua),
      json: Boolean(node?.scriptFormats?.json),
    },
    scriptAttached: Boolean(
      node?.scriptAttached ||
      node?.EmbeddedScript?.content ||
      node?.scriptFormats?.lua ||
      node?.scriptFormats?.json
    ),
    packageName: node?.packageName || node?.PackageName || node?.EmbeddedScript?.packageName || '',
    testedWithRuntimeGo: Boolean(node?.testedWithRuntimeGo ?? node?.TestedWithRuntimeGo),
  };
};

// Flatten RTDB products branch into normalized array with new structure
export const normalizeProductsFromRTDB = (productsRoot) => {
  if (!productsRoot || typeof productsRoot !== 'object') return [];

  const out = [];
  for (const category of Object.keys(productsRoot)) {
    const group = productsRoot[category] || {};
    for (const id of Object.keys(group)) {
      const normalized = normalizeProductFromRTDB(category, id, group[id]);
      if (normalized) {
        out.push(normalized);
        (normalized.addons || []).forEach((addon) => {
          out.push({
            ...addon,
            id: `${normalized.id}::addon::${addon.id}`,
            addonId: addon.id,
            baseProductId: normalized.id,
            baseProductName: normalized.name,
            category: 'addon',
            name: addon.name,
            description: addon.description || `Addon for ${normalized.name}`,
            image: addon.image || normalized.image,
            pricingRaw: addon.pricingRaw,
            price: addon.price,
            status: addon.status,
            parentProduct: normalized,
            routeProductId: normalized.id,
            featured: false,
            popularity: normalized.popularity || 0
          });
        });
      }
    }
  }
  return out;
};

// Map Admin.carouselImages {} into array of URLs
// Map Admin.carouselImages {} into array of standard slide objects
export const normalizeCarouselFromRTDB = (data) => {
  if (!data) return [];

  // 1. JSON String Support
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      if (Array.isArray(parsed)) return parsed;
    } catch (e) {
      return [];
    }
  }

  // 2. Legacy Object Support { id: { url: "..." } }
  if (typeof data === 'object') {
    return Object.values(data)
      .map((n) => {
        if (n?.url) return { image: n.url };
        return null;
      })
      .filter(Boolean);
  }

  return [];
};

// Normalize admin.settings with new structure
const normalizeDownloadItemsFromSettings = (settings = {}) => {
  const normalizeItems = (items) => {
    if (!Array.isArray(items)) return [];
    return items
      .map((item, index) => {
        if (!item || typeof item !== 'object') return null;
        const name = typeof item.name === 'string' ? item.name.trim() : '';
        const description = typeof item.description === 'string' ? item.description.trim() : '';
        const link = typeof item.link === 'string' ? item.link.trim() : '';
        const type = typeof item.type === 'string' ? item.type.trim().toLowerCase() : '';
        if (!link) return null;
        return {
          name: name || `Download ${index + 1}`,
          description,
          link,
          type: type === 'lua' || type === 'json' || type === 'free' ? type : '',
        };
      })
      .filter(Boolean);
  };

  const raw = settings?.downloadItems;
  if (Array.isArray(raw)) {
    const normalized = normalizeItems(raw);
    if (normalized.length) return normalized;
  }

  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      const normalized = normalizeItems(parsed);
      if (normalized.length) return normalized;
    } catch {
      // ignore invalid JSON and fallback
    }
  }

  if (raw && typeof raw === 'object') {
    const normalized = normalizeItems(Object.values(raw));
    if (normalized.length) return normalized;
  }

  return [];
};

export const resolveScriptDownloadItems = (settings = {}) => {
  const items = normalizeDownloadItemsFromSettings(settings);
  const luaItems = items.filter((item) => item.type === 'lua');
  const jsonItems = items.filter((item) => item.type === 'json');
  const freeItems = items.filter((item) => item.type === 'free');
  const fallbackItems = items.filter((item) => !item.type);

  return {
    luaItems,
    jsonItems,
    freeItems,
    fallbackItems,
    allItems: items,
  };
};

export const normalizeSettingsFromRTDB = (settings) => {
  const parseNum = (val, fallback) => {
    const n = Number(val);
    return isNaN(n) ? fallback : n;
  };

  const parseJsonObject = (value, fallback = {}) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) return value;
    if (typeof value === 'string' && value.trim()) {
      try {
        const parsed = JSON.parse(value);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
      } catch {
        return fallback;
      }
    }
    return fallback;
  };

  return {
    siteName: settings?.siteName || settings?.SiteName || 'PSH STORE',
    currency: settings?.currency || 'USDT',
    commission: parseNum(settings?.commission ?? settings?.taxRate, 15),
    exchangeRateINR: parseNum(settings?.exchangeRateINR, 92),
    pointsRate: parseNum(settings?.pointsRate, 20),
    referralBonus: parseNum(settings?.referralBonus, 10),
    topUpBonusPercent: Math.max(0, parseNum(settings?.topUpBonusPercent, 0)),
    scriptRequestFee: Math.max(0, parseNum(settings?.scriptRequestFee, 5)),
    scriptRequestMinAmount: Math.max(0, parseNum(settings?.scriptRequestMinAmount, 5)),
    extensionDiscount: parseJsonObject(settings?.extensionDiscount, {}),
    downloadLink: settings?.downloadLink || '',
    universalScriptLink: settings?.universalScriptLink || '',
    downloadItems: normalizeDownloadItemsFromSettings(settings),
    developmentMode: Boolean(settings?.developmentMode ?? settings?.DevelopmentMode ?? false),
  };
};

// Get trending products from stats data
export const getTrendingProducts = (stats, products) => {
  if (!stats?.ProductSales || !products) return [];

  const productSales = stats.ProductSales;

  // Convert to array and sort by sales count
  const trendingData = Object.entries(productSales)
    .map(([productId, salesCount]) => ({
      productId,
      salesCount: Number(salesCount) || 0
    }))
    .sort((a, b) => b.salesCount - a.salesCount)
    .slice(0, 4); // Get top 4

  // Find corresponding product data
  const trendingProducts = [];

  for (const { productId } of trendingData) {
    // Search in all product categories
    let product = null;

    if (Array.isArray(products)) {
      product = products.find(p => p.id === productId);
    } else {
      // Search in categorized products
      const allProducts = [
        ...(products.premium || []),
        ...(products.exclusive || []),
        ...(products.bundle || []),
        ...(products.freeScripts || []),
        ...(products.others || [])
      ];
      product = allProducts.find(p => p.id === productId);
    }

    if (product) {
      trendingProducts.push({
        ...product,
        trending: true
      });
    }
  }

  return trendingProducts;
};

export const getTrendingProductsByWindow = (stats, products, windowDays = 7) => {
  const allProducts = Array.isArray(products)
    ? products
    : [
        ...(products?.premium || []),
        ...(products?.exclusive || []),
        ...(products?.bundle || []),
        ...(products?.freeScripts || []),
        ...(products?.others || []),
      ];

  if (!allProducts.length) return [];

  const byDay = stats?.ProductSalesByDay;
  if (!byDay || typeof byDay !== 'object') {
    return getTrendingProducts(stats, allProducts);
  }

  const requestedDays = Number(windowDays);
  if (requestedDays <= 0) {
    const lifetime = stats?.ProductSales;
    if (lifetime && typeof lifetime === 'object') {
      const ranked = Object.entries(lifetime)
        .map(([productId, count]) => ({ productId, count: Number(count) || 0 }))
        .filter((entry) => entry.count > 0)
        .sort((a, b) => b.count - a.count)
        .slice(0, 4);

      return ranked
        .map(({ productId }) => allProducts.find((p) => p.id === productId))
        .filter(Boolean)
        .map((p) => ({ ...p, trending: true }));
    }
    return getTrendingProducts(stats, allProducts);
  }
  const safeDays = Math.max(1, requestedDays || 7);
  const now = new Date();
  const counts = new Map();

  for (let offset = 0; offset < safeDays; offset += 1) {
    const date = new Date(now);
    date.setUTCDate(now.getUTCDate() - offset);
    const key = date.toISOString().slice(0, 10);
    const dayBucket = byDay[key];
    if (!dayBucket || typeof dayBucket !== 'object') continue;

    Object.entries(dayBucket).forEach(([productId, value]) => {
      const next = (counts.get(productId) || 0) + (Number(value) || 0);
      counts.set(productId, next);
    });
  }

  const sorted = Array.from(counts.entries())
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4);

  if (!sorted.length) {
    return getTrendingProducts(stats, allProducts);
  }

  return sorted
    .map(([productId]) => allProducts.find((p) => p.id === productId))
    .filter(Boolean)
    .map((p) => ({ ...p, trending: true }));
};

// Optional: throttle helper
export const throttle = (func, limit = 200) => {
  let inThrottle = false;
  return (...args) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => { inThrottle = false; }, limit);
    }
  };
};

export const normalizeUserRecord = (node, fallback = {}) => {
  if (!node || typeof node !== 'object') return { ...fallback };

  const resolveTelegramUsername = (...candidates) => {
    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
    }
    return '';
  };

  const extractUsedCoupons = (source) => {
    if (!source || typeof source !== 'object') return {};
    const raw =
      source['Coupons Used'] ||
      source.usedCoupons ||
      source.couponsUsed ||
      {};
    return raw && typeof raw === 'object' ? raw : {};
  };

  if (node['Personal details'] || node['Referral details']) {
    const personal = node['Personal details'] || {};
    const referral = node['Referral details'] || {};
    const usedCoupons = extractUsedCoupons(node);
    const telegramUsername = resolveTelegramUsername(
      personal['Telegram Username'],
      node.telegramUsername,
      fallback.telegramUsername
    );

    return {
      ...fallback,
      name: personal.Name ?? fallback.name,
      email: personal.Email ?? fallback.email,
      createdAt: personal['Signup date'] || node.createdAt || fallback.createdAt,
      telegramUsername,
      telegramChatId:
        node.TelegramChatId ??
        node.telegramChatId ??
        node['Telegram Chat ID'] ??
        personal.TelegramChatId ??
        personal.telegramChatId ??
        personal['Telegram Chat ID'] ??
        fallback.telegramChatId ??
        '',
      telegramBoundAt:
        node.TelegramBoundAt ??
        node.telegramBoundAt ??
        personal.TelegramBoundAt ??
        personal.telegramBoundAt ??
        fallback.telegramBoundAt ??
        null,
      referralCode: referral['Referral Code'] ?? node.referralCode ?? fallback.referralCode ?? '',
      referredBy: referral['Referred By'] ?? node.referredBy ?? fallback.referredBy ?? '',
      orders: node.Orders ?? fallback.orders ?? {},
      totalSpent: node['Total Spent'] ?? node.totalSpent ?? fallback.totalSpent ?? 0,
      rewardPoints: node['Reward Points'] ?? node.rewardPoints ?? fallback.rewardPoints ?? 0,
      usedCoupons: {
        ...(fallback.usedCoupons || {}),
        ...usedCoupons,
      },
      country: personal.Country ?? fallback.country ?? '',
      language: personal.Language ?? fallback.language ?? '',
      timezone: personal.Timezone ?? fallback.timezone ?? '',
      isDisabled: Boolean(node.isDisabled),
    };
  }

  const telegramUsername = resolveTelegramUsername(node.telegramUsername, fallback.telegramUsername);

  return {
    ...fallback,
    ...node,
    telegramUsername,
    telegramChatId:
      node.TelegramChatId ??
      node.telegramChatId ??
      node['Telegram Chat ID'] ??
      fallback.telegramChatId ??
      '',
    telegramBoundAt:
      node.TelegramBoundAt ??
      node.telegramBoundAt ??
      fallback.telegramBoundAt ??
      null,
    referralCode: node.referralCode ?? fallback.referralCode ?? '',
    referredBy: node.referredBy ?? fallback.referredBy ?? '',
    usedCoupons: {
      ...(fallback.usedCoupons || {}),
      ...extractUsedCoupons(node),
    },
    country: node.Country ?? fallback.country ?? '',
    language: node.Language ?? fallback.language ?? '',
    timezone: node.Timezone ?? fallback.timezone ?? '',
    isDisabled: Boolean(node.isDisabled),
  };
};

// Helper to check if item matches restrictions
export const isItemEligibleForCoupon = (item, coupon) => {
  if (!coupon || !item) return false;
  if (item?.itemType === 'script-request' || item?.category === 'script-request') return false;

  const parseCsvOrArray = (value) => {
    if (Array.isArray(value)) return value.map((entry) => String(entry || '').trim()).filter(Boolean);
    if (typeof value === 'string') return value.split(',').map((entry) => entry.trim()).filter(Boolean);
    return [];
  };

  const allowedProductIds = [
    ...parseCsvOrArray(coupon.productId),
    ...parseCsvOrArray(coupon.productIds),
    ...parseCsvOrArray(coupon.allowedProductIds),
    ...parseCsvOrArray(coupon.allowedProducts)
  ].map(String);

  if (allowedProductIds.length > 0 && !allowedProductIds.some(id => id.toLowerCase() === 'all' || id === '*')) {
    const itemProductIds = [
      item.productId,
      item.ProductId,
      item.id,
      item.addonFor,
      item.baseProductId,
      item.parentProductId
    ].filter(Boolean).map((value) => String(value).replace(/-extension$/, ''));
    if (!itemProductIds.some((id) => allowedProductIds.includes(id))) {
      return false;
    }
  }

  // 1. Period/Validity Check
  const allowedPeriods = [
    ...parseCsvOrArray(coupon.period),
    ...parseCsvOrArray(coupon.periods),
    ...parseCsvOrArray(coupon.allowedPeriods),
    ...parseCsvOrArray(coupon.allowedPeriod)
  ].map((entry) => String(entry || '').toLowerCase());

  if (allowedPeriods.length > 0 && !allowedPeriods.some(p => p === 'all' || p === '*')) {
    const itemValidity = (item.validity || '').toLowerCase();
    const normalizedItemValidity = normalizeDurationKey(itemValidity);
    const normalizedAllowedPeriods = allowedPeriods.map(normalizeDurationKey);
    if (!itemValidity || (!allowedPeriods.includes(itemValidity) && !normalizedAllowedPeriods.includes(normalizedItemValidity))) {
      return false;
    }
  }

  // 2. Category Check
  const allowedCategories = [
    ...parseCsvOrArray(coupon.category),
    ...parseCsvOrArray(coupon.categories),
    ...parseCsvOrArray(coupon.allowedCategories),
    ...parseCsvOrArray(coupon.allowedCategory)
  ].map((entry) => String(entry || '').toLowerCase());

  if (allowedCategories.length > 0 && !allowedCategories.some(c => c === 'all' || c === '*')) {
    const itemCategory = normalizePremiumEquivalentCategory(item.category);
    const rawCategory = String(item.category || '').toLowerCase();
    if (!itemCategory || (!allowedCategories.includes(itemCategory) && !allowedCategories.includes(rawCategory))) {
      return false;
    }
  }

  return true;
};

export const parseCouponRestrictionList = (value) => {
  if (Array.isArray(value)) return value.map((entry) => String(entry || '').trim()).filter(Boolean);
  if (typeof value === 'string') return value.split(',').map((entry) => entry.trim()).filter(Boolean);
  return [];
};

export const getCouponAllowedUserIds = (coupon = {}) => [
  ...parseCouponRestrictionList(coupon.userId),
  ...parseCouponRestrictionList(coupon.userIds),
  ...parseCouponRestrictionList(coupon.allowedUserId),
  ...parseCouponRestrictionList(coupon.allowedUserIds),
  ...parseCouponRestrictionList(coupon.allowedUsers)
].map(String);

export const getCouponAllowedProductIds = (coupon = {}) => [
  ...parseCouponRestrictionList(coupon.productId),
  ...parseCouponRestrictionList(coupon.productIds),
  ...parseCouponRestrictionList(coupon.allowedProductIds),
  ...parseCouponRestrictionList(coupon.allowedProducts)
].map(String);

export const isCouponVisibleToUser = (coupon = {}, currentUser = null) => {
  if (!coupon || typeof coupon !== "object") return false;

  const allowedUserIds = getCouponAllowedUserIds(coupon);
  const isUserSpecific = coupon.visibility === "user_specific" || coupon.isUserAssigned === true || allowedUserIds.length > 0;

  // 1. User-specific coupons: visible ONLY to the allowed user (even if stored under PrivateCoupons)
  if (isUserSpecific) {
    const currentUserId = String(
      (typeof currentUser === "string" ? currentUser : (currentUser?.uid || currentUser?.id || currentUser?.userId)) || ""
    );
    if (!currentUserId) return false;
    if (allowedUserIds.length > 0) {
      return allowedUserIds.includes(currentUserId);
    }
    return Boolean(coupon.isUserAssigned);
  }

  // 2. Purely private coupons: strictly hidden from storefront list views
  if (coupon.isPrivate === true || coupon.visibility === "private" || coupon.storage === "private") {
    return false;
  }

  // 3. Public coupons: visible to everyone
  return true;
};

// Validate coupon code
export const validateCoupon = (couponCode, coupons, context, userUsedCoupons = {}, currentUser = null) => {
  const cartitems = Array.isArray(context) ? context : (context?.items || []);
  const cartTotal = typeof context === 'number' ? context : (context?.total || 0);

  if (!couponCode) return { error: 'Please enter a coupon code' };
  if (!Array.isArray(coupons) || coupons.length === 0) {
    return { error: 'No coupons available right now' };
  }

  const normalizedCode = String(couponCode).trim().toUpperCase();
  if (!normalizedCode) return { error: 'Invalid coupon code' };

  const coupon = coupons.find(
    (c) => (c.code || '').toUpperCase() === normalizedCode
  );
  if (!coupon) return { error: `Coupon code "${normalizedCode}" is not valid` };

  if (coupon.isArchived || (coupon.status && coupon.status.toLowerCase() === 'archived')) {
    return { error: "This coupon is no longer available" };
  }

  if (coupon.status && coupon.status.toLowerCase() !== 'active') {
    return { error: `This coupon is ${coupon.status.toLowerCase()} and cannot be used` };
  }

  if (coupon.firstOrderOnly) {
    const priorOrders = Number(
      currentUser?.["Total order"] ??
      currentUser?.totalOrders ??
      currentUser?.total_orders ??
      (Array.isArray(currentUser?.orders) ? currentUser.orders.length : 0)
    ) || 0;
    if (priorOrders > 0) {
      return { error: "This coupon is valid for your first order only" };
    }
  }

  const now = new Date();
  if (coupon.startDate && new Date(coupon.startDate) > now) {
    return { error: `This coupon starts on ${new Date(coupon.startDate).toLocaleDateString()}` };
  }
  if (coupon.endDate && new Date(coupon.endDate) < now) {
    return { error: `This coupon expired on ${new Date(coupon.endDate).toLocaleDateString()}` };
  }

  const usageLimit = Number(coupon.usageLimit ?? 0) || 0;
  const timesUsed = Number(coupon.timesUsed ?? 0) || 0;
  if (usageLimit > 0 && timesUsed >= usageLimit) {
    return { error: `This coupon has reached its usage limit (${usageLimit} uses)` };
  }

  const allowedUserIds = getCouponAllowedUserIds(coupon);

  if (allowedUserIds.length > 0) {
    const currentUserId = String(
      (typeof currentUser === 'string' ? currentUser : (currentUser?.uid || currentUser?.id || currentUser?.userId)) || ''
    );
    if (!currentUserId || !allowedUserIds.includes(currentUserId)) {
      return { error: 'This coupon is locked to the approved requester account' };
    }
  }

  // Calculate eligible total if restrictions exist
  let eligibleTotal = cartTotal;

  // Parse restrictions for check
  const allowedPeriods = [
    ...parseCouponRestrictionList(coupon.period),
    ...parseCouponRestrictionList(coupon.periods),
    ...parseCouponRestrictionList(coupon.allowedPeriods),
    ...parseCouponRestrictionList(coupon.allowedPeriod)
  ].map(s => s.toLowerCase());

  const allowedCategories = [
    ...parseCouponRestrictionList(coupon.category),
    ...parseCouponRestrictionList(coupon.categories),
    ...parseCouponRestrictionList(coupon.allowedCategories),
    ...parseCouponRestrictionList(coupon.allowedCategory)
  ].map(s => s.toLowerCase());

  const allowedProductIds = getCouponAllowedProductIds(coupon);

  const hasPeriodRestriction = allowedPeriods.length > 0 && !allowedPeriods.some(p => p === 'all' || p === '*');
  const hasCategoryRestriction = allowedCategories.length > 0 && !allowedCategories.some(c => c === 'all' || c === '*');
  const hasProductRestriction = allowedProductIds.length > 0 && !allowedProductIds.some(p => p.toLowerCase() === 'all' || p === '*');

  const hasRestrictions = hasPeriodRestriction || hasCategoryRestriction || hasProductRestriction;

  if (hasRestrictions && cartitems.length > 0) {
    eligibleTotal = cartitems.reduce((sum, item) => {
      if (isItemEligibleForCoupon(item, coupon)) {
        const price = typeof item.price === 'string'
          ? parseFloat(item.price.replace(/[^0-9.]/g, ''))
          : item.price;
        return sum + (price || 0);
      }
      return sum;
    }, 0);

    if (eligibleTotal === 0) {
      // Build detailed error message
      let errorParts = [];

      if (allowedPeriods.length > 0) {
        const periodsText = allowedPeriods.map(formatDurationLabel).join(', ');
        errorParts.push(`valid for ${periodsText} plans only`);
      }

      if (allowedCategories.length > 0) {
        const categoriesText = allowedCategories.map(c => c.charAt(0).toUpperCase() + c.slice(1)).join(', ');
        errorParts.push(`valid for ${categoriesText} category only`);
      }

      if (allowedProductIds.length > 0) {
        errorParts.push('valid only for the approved requested product');
      }

      const restrictionText = errorParts.join(' and ');
      return { error: `This coupon is ${restrictionText}. Your cart doesn't contain any eligible items.` };
    }
  }

  const minPurchase = Number(coupon.minPurchaseAmount ?? 0) || 0;
  if (minPurchase > 0 && eligibleTotal < minPurchase) {
    const currencyLabel = coupon.currency || 'USDT';
    const shortfall = minPurchase - eligibleTotal;
    return {
      error: `Minimum purchase of ${minPurchase.toFixed(2)} ${currencyLabel} required. Add ${shortfall.toFixed(2)} ${currencyLabel} more in eligible items.`,
    };
  }

  const isCountedCouponUsage = (usage) => {
    if (!usage || typeof usage !== 'object') return false;
    const status = String(usage.status || '').trim().toLowerCase();
    if (!status) return true;
    return ['pending', 'approved', 'used', 'completed'].includes(status);
  };

  // --- Check User Usage Limit ---
  // Get usage record for this specific coupon code
  const usageRecord = userUsedCoupons ? userUsedCoupons[normalizedCode] : null;

  // Calculate how many times user has used this
  let timesUsedByUser = 0;
  if (usageRecord) {
    // 1. Check for legacy usage (direct user properties)
    if ((usageRecord.usedAt || usageRecord.orderId) && isCountedCouponUsage(usageRecord)) {
      timesUsedByUser += 1;
    }

    // 2. Check for nested usages (keys that are objects, excluding legacy fields)
    const reservedKeys = new Set(['orderId', 'usedAt', 'discount', 'usedBy', 'status', 'approvedAt', 'releasedAt', 'releaseReason']);
    const nestedUsages = Object.keys(usageRecord).filter(k =>
      !reservedKeys.has(k) &&
      typeof usageRecord[k] === 'object' &&
      usageRecord[k] !== null &&
      isCountedCouponUsage(usageRecord[k])
    );

    timesUsedByUser += nestedUsages.length;
  }

  const maxUsagePerUser = Number(coupon.maxUsagePerUser ?? 1); // Default to 1 if not set

  if (timesUsedByUser >= maxUsagePerUser) {
    return { error: 'You have reached the usage limit for this coupon' };
  }

  return {
    coupon: {
      ...coupon,
      code: normalizedCode,
      timesUsed,
    },
    valid: true,
  };
};

// Calculate discount from coupon
export const calculateCouponDiscount = (coupon, context) => {
  if (!coupon) return 0;

  // Accept cartItems list or simple subtotal value
  let eligibleSubtotal = 0;

  if (Array.isArray(context)) {
    // Context is items array
    eligibleSubtotal = context.reduce((sum, item) => {
      if (isItemEligibleForCoupon(item, coupon)) {
        const price = typeof item.price === 'string' ? parseFloat(item.price.replace(/[^0-9.]/g, '')) : (item.price || 0);
        return sum + price;
      }
      return sum;
    }, 0);

  } else if (typeof context === 'number') {
    // Legacy support / fallback
    eligibleSubtotal = context; // Assume all eligible if items not provided
  } else if (typeof context === 'object' && context.items) {
    // object with items
    return calculateCouponDiscount(coupon, context.items);
  }

  // If no items are eligible, 0 discount
  if (eligibleSubtotal <= 0) return 0;

  let discount = 0;
  if (coupon.type === 'percentage') {
    discount = (eligibleSubtotal * coupon.value) / 100;
  } else if (coupon.type === 'fixed') {
    discount = Math.min(coupon.value, eligibleSubtotal);
  }

  // New 'max' field support (User Schema) or legacy 'maxDiscount'
  const maxCap = coupon.max !== undefined ? Number(coupon.max) : (coupon.maxDiscount || 0);

  if (maxCap > 0) {
    return Math.min(discount, maxCap, eligibleSubtotal);
  }

  return Math.min(discount, eligibleSubtotal);
};

// Calculate discount from reward points
export const calculateRewardPointsDiscount = (points, pointsRate = 20) => {
  if (!points || points <= 0) return 0;
  const rate = Number(pointsRate) || 20;
  return points / rate;
};

// Validate reward points usage
export const validateRewardPointsUsage = (pointsToUse, availablePoints, cartTotal, pointsRate = 20) => {
  // Convert inputs to numbers and validate
  const pointsNumber = parseInt(pointsToUse) || 0;
  const availableNumber = parseInt(availablePoints) || 0;
  const cartNumber = parseFloat(cartTotal) || 0;
  const rate = Number(pointsRate) || 20;

  if (pointsNumber <= 0) {
    return { valid: false, error: 'Please enter a valid points amount' };
  }

  if (pointsNumber > availableNumber) {
    return {
      valid: false,
      error: `Insufficient points. You have ${availableNumber} points available.`
    };
  }

  const discountAmount = calculateRewardPointsDiscount(pointsNumber, rate);
  if (discountAmount > cartNumber) {
    return {
      valid: false,
      error: `Points discount ($${discountAmount.toFixed(2)}) cannot exceed cart total ($${cartNumber.toFixed(2)}).`
    };
  }

  return {
    valid: true,
    discountAmount
  };
};

// Get maximum usable points for cart total
export const getMaxUsablePoints = (availablePoints, cartTotal, pointsRate = 20) => {
  const availableNumber = parseInt(availablePoints) || 0;
  const cartNumber = parseFloat(cartTotal) || 0;
  const rate = Number(pointsRate) || 20;

  if (availableNumber <= 0 || cartNumber <= 0) return 0;

  // Maximum points that can be used based on cart total
  // dynamic rate points = 1 USDT, 1 point = 1/rate USDT
  // Max points needed for cart = cartTotal * rate
  const maxPointsForCart = Math.ceil(cartNumber * rate);

  // Return limiting factor: either total available points or points needed for full cart coverage
  return Math.min(availableNumber, maxPointsForCart);
};

export const calculateRemainingDays = (approvalDate, validity, currentDate) => {
  if (!approvalDate || !validity || !currentDate) {
    return null;
  }

  let approval;

  if (approvalDate instanceof Date) {
    approval = new Date(approvalDate);
  } else if (typeof approvalDate === 'number') {
    approval = new Date(approvalDate);
  } else if (typeof approvalDate === 'string') {
    const parts = approvalDate.trim().split(' ');
    if (parts.length >= 2 && parts[0].includes(':')) {
      const dateParts = parts[0].split(':');
      if (dateParts.length === 3) {
        approval = new Date(dateParts[2], dateParts[1] - 1, dateParts[0]);
      } else {
        approval = new Date(approvalDate);
      }
    } else {
      approval = new Date(approvalDate);
    }
  } else {
    return null;
  }

  if (!approval || isNaN(approval.getTime())) {
    return null;
  }

  // Set to start of day for date-only comparison
  approval.setHours(0, 0, 0, 0);

  const durationDays = getDurationDays(validity);
  if (!Number.isFinite(durationDays)) return null;

  const expiration = new Date(approval);
  expiration.setDate(expiration.getDate() + durationDays);

  // Set current date to start of day for comparison
  const today = new Date(currentDate);
  today.setHours(0, 0, 0, 0);

  const remainingTime = expiration.getTime() - today.getTime();
  const remainingDays = Math.ceil(remainingTime / (1000 * 60 * 60 * 24));

  return remainingDays > 0 ? remainingDays : 0;
};

export const calculateExtensionPrice = (basePrice, validity, extensionDiscountConfig = {}) => {
  const price = Number(basePrice) || 0;
  const period = normalizeDurationKey(validity);
  const rawPeriod = String(validity || '').toLowerCase().trim();
  const keys = [
    `extension-${rawPeriod}`,
    `extension-${period}`,
  ];
  if (period === 'monthly') keys.push('extension-1m');
  if (period === 'quarterly') keys.push('extension-3m');
  if (period === 'yearly') keys.push('extension-1y');
  if (period === '3d' || period === '3_days') keys.push('extension-3d', 'extension-3_days');
  if (period === '7d' || period === '7_days') keys.push('extension-7d', 'extension-7_days');
  if (period === '15d' || period === '15_days') keys.push('extension-15d', 'extension-15_days');
  const rawValue = keys.map((key) => extensionDiscountConfig?.[key]).find((value) => value !== undefined);

  let discountPercent = 0;
  if (typeof rawValue === 'string') {
    discountPercent = parseFloat(rawValue.replace('%', ''));
  } else if (typeof rawValue === 'number') {
    discountPercent = rawValue;
  }

  if (!Number.isFinite(discountPercent) || discountPercent <= 0) {
    return price;
  }

  const normalizedPercent = Math.max(0, Math.min(100, discountPercent));
  const discounted = price - ((price * normalizedPercent) / 100);
  return Math.max(0, Number(discounted.toFixed(2)));
};

// Redirect to the new atomic integer timestamp
export const getIndiaDateTime = async () => {
  return getSecureAtomicTime();
};
